import React, { useRef, useState, useEffect } from 'react';
import { ResourceManager } from './components/ResourceManager';
import { useSupabaseResource } from '@/hooks/useSupabaseResource';
import { supabase } from '@/lib/supabaseClient';
import { Download, Upload } from 'lucide-react';

const BlogsModule = () => {
  const { records, refresh } = useSupabaseResource('blog_posts');
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const fileInputRef = useRef();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const { data, error } = await supabase
      .from('blog_categories')
      .select('*')
      .eq('is_active', true)
      .order('display_order');
    if (!error) {
      setCategories(data || []);
    }
  };

  const exportToCSV = () => {
    const headers = ['title', 'author', 'category_id', 'date', 'image', 'excerpt', 'content', 'is_featured', 'published'];
    const csvContent = [
      headers.join(','),
      ...records.map(record => headers.map(h => `"${record[h] || ''}"`).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'blog_posts.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());
      if (lines.length < 2) {
        alert('CSV must have headers and at least one row');
        return;
      }
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim());
      const requiredHeaders = ['title', 'author', 'date', 'excerpt', 'content'];
      const missing = requiredHeaders.filter(h => !headers.includes(h));
      if (missing.length) {
        alert(`Missing required headers: ${missing.join(', ')}`);
        return;
      }
      const blogs = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.replace(/"/g, '').trim());
        const obj = {};
        headers.forEach((h, i) => {
          obj[h] = values[i] || '';
        });
        // transform
        obj.is_featured = obj.is_featured === 'true';
        obj.published = obj.published === 'true';
        obj.category_id = obj.category_id ? obj.category_id : null;
        return obj;
      });
      const { error } = await supabase.from('blog_posts').insert(blogs);
      if (error) throw error;
      alert('Bulk upload successful');
      refresh();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleBulkUpload}
        accept=".csv"
        style={{ display: 'none' }}
      />
      <ResourceManager
        table="blog_posts"
        title="Blog Posts"
        description="Publish editorial stories that surface on nyinimu.com/blog."
        columns={[
          { label: 'Title', accessor: 'title' },
          { label: 'Author', accessor: 'author' },
          {
            label: 'Category',
            accessor: 'category_id',
            render: (value) => {
              const category = categories.find(cat => cat.id === value);
              return category ? category.name : 'Uncategorized';
            },
          },
          {
            label: 'Date',
            accessor: 'date',
            render: (value) => (value ? new Date(value).toLocaleDateString() : '—'),
          },
          {
            label: 'Featured',
            accessor: 'is_featured',
            render: (value) => (value ? 'Yes' : 'No'),
          },
          {
            label: 'Published',
            accessor: 'published',
            render: (value) => (value ? 'Live' : 'Draft'),
          },
        ]}
        formFields={[
          { name: 'title', label: 'Title', placeholder: 'Nyinimu Launches Daily Ownership' },
          { name: 'author', label: 'Author', placeholder: 'Nyinimu Editorial Team' },
          { name: 'category_id', label: 'Category', type: 'select', options: categories.map(cat => ({
            label: cat.name,
            value: cat.id
          }))},
          { name: 'date', label: 'Publish Date', type: 'date' },
          { name: 'image', label: 'Cover Image URL', placeholder: 'https://...' },
          { name: 'excerpt', label: 'Excerpt', type: 'textarea', rows: 3 },
          { name: 'content', label: 'Full Content (HTML or Markdown)', type: 'textarea', rows: 8 },
          { name: 'is_featured', label: 'Mark as Featured', type: 'checkbox' },
          { name: 'published', label: 'Mark as Published', type: 'checkbox' },
        ]}
        defaultValues={{
          title: '',
          author: 'Nyinimu Editorial Team',
          category_id: '',
          date: new Date().toISOString().slice(0, 10),
          image: '',
          excerpt: '',
          content: '',
          is_featured: true,
          published: true,
        }}
        extraButtons={[
          { label: 'Export CSV', onClick: exportToCSV, icon: Download },
          { label: uploading ? 'Uploading...' : 'Bulk Upload', onClick: () => fileInputRef.current.click(), disabled: uploading, icon: Upload },
        ]}
      />
    </>
  );
};

export default BlogsModule;
